import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import LoadingIndicator from 'components/LoadingIndicator';
import PaymentIcon from 'components/PaymentIcon';
import { useGetEscrowByTradeQuery } from 'api/queries/escrow/get-escrow-by-trade';
import { EscrowPayoutRoleSummary, EscrowPayouts, PaymentOrderStatus } from 'api/escrow/types';
import { useDataValues } from 'providers/DataValues';
import { OrderStatus, PAYMENT_ORDER_STATUS_LABELS, PaymentOrderStatusEnum } from 'utils/enums';
import { getPaymentLabel } from 'utils/provider-labels';
import { currencyFormatter } from 'utils/currency-formatter';
import { memoryQueryClient } from 'providers/TanstackQuery';
import { getOrderBaseQueryKey } from 'api/queries/product/get-order';
import { myTradesAllQueryKey } from 'api/queries/product/my-trades';
import { getActiveDispute, hasOnlyTerminalDisputes } from 'utils/escrow-helpers';
import {
  getFailureCodeLabel,
  getPayoutShapeVersion,
  getSettlementBadge,
  PAYOUT_COMPONENT_LABELS,
  PAYOUT_ROLE_STATUS_COLORS,
  PAYOUT_ROLE_STATUS_LABELS,
  PAYOUT_STATUS_COLORS,
  PAYOUT_STATUS_LABELS,
} from 'utils/payout-status';
import { KEYS, logEvent } from 'utils/analytics';
import { getPaymentDriverIconUrl, getPaymentMethodIconUrl } from 'utils/payment-helpers';

type EscrowPanelProps = {
  tradeId: number;
  tradeStatus: OrderStatus;
};

const PAYMENT_ORDER_STATUS_COLORS: Record<PaymentOrderStatus, string> = {
  canceled: 'text-gray-500',
  paid: 'text-green-600',
  partially_paid: 'text-blue-600',
  pending: 'text-yellow-600',
  refunded: 'text-purple-600',
};

const PAYMENT_ORDER_STATUS_DISPLAY_LABELS: Record<PaymentOrderStatus, string> = {
  canceled: PAYMENT_ORDER_STATUS_LABELS[PaymentOrderStatusEnum.Canceled],
  paid: PAYMENT_ORDER_STATUS_LABELS[PaymentOrderStatusEnum.Paid],
  partially_paid: PAYMENT_ORDER_STATUS_LABELS[PaymentOrderStatusEnum.PartiallyPaid],
  pending: PAYMENT_ORDER_STATUS_LABELS[PaymentOrderStatusEnum.Pending],
  refunded: PAYMENT_ORDER_STATUS_LABELS[PaymentOrderStatusEnum.Refunded],
};

export default function EscrowPanel({ tradeId, tradeStatus }: EscrowPanelProps) {
  const { userId } = useDataValues();

  const { data: escrow, isLoading } = useGetEscrowByTradeQuery(tradeId, {
    enabled: true,
    payoutPolling: tradeStatus === OrderStatus.Completed,
    polling: tradeStatus === OrderStatus.PendingFunding,
  });

  const prevActivatedRef = useRef(escrow?.activatedAt);

  useEffect(() => {
    if (escrow?.activatedAt && !prevActivatedRef.current) {
      memoryQueryClient.refetchQueries({ queryKey: getOrderBaseQueryKey, exact: false });
      memoryQueryClient.refetchQueries({ queryKey: myTradesAllQueryKey, exact: false });
    }
    prevActivatedRef.current = escrow?.activatedAt;
  }, [escrow?.activatedAt]);

  if (isLoading) {
    return (
      <div className={'bg-brand-purple-card rounded-lg p-4 md:p-6 flex items-center justify-center'}>
        <LoadingIndicator size={32} className={'text-brand-black'} />
      </div>
    );
  }

  if (!escrow) {
    return null;
  }

  const numericUserId = +userId;
  const isBuyer = escrow.buyerId === numericUserId;
  const myPaymentOrder = escrow.paymentOrders.find((po) => po.userId === numericUserId);

  if (!myPaymentOrder) {
    return null;
  }

  const statusLabel = PAYMENT_ORDER_STATUS_DISPLAY_LABELS[myPaymentOrder.status];
  const statusColorClass = PAYMENT_ORDER_STATUS_COLORS[myPaymentOrder.status];

  // The "Paid via …" label and method/driver icons are only shown when the order is in a
  // paid-related state; refunded/cancelled orders hide this to avoid a misleading line.
  // Method/driver/timestamp now sourced directly from the PaymentOrder row (see US-FE-002).
  const showPaymentDetail = 'paid' === myPaymentOrder.status || 'partially_paid' === myPaymentOrder.status;
  const driverIconUrl = myPaymentOrder.paymentDriver ? getPaymentDriverIconUrl(myPaymentOrder.paymentDriver) : null;
  const methodIconUrl = myPaymentOrder.paymentMethod ? getPaymentMethodIconUrl(myPaymentOrder.paymentMethod) : null;

  // True when the PO has been (or was) settled — paid, partially paid, or refunded.
  // Refunded is included so a refunded-then-closed escrow still suppresses the
  // "no longer required" copy below; canceled is excluded because canceled-without-payment
  // is the canonical case that copy is designed for.
  const anyPaymentEverHeld =
    'paid' === myPaymentOrder.status ||
    'partially_paid' === myPaymentOrder.status ||
    'refunded' === myPaymentOrder.status;

  const counterpartyStatusLabel = escrow.counterParty
    ? PAYMENT_ORDER_STATUS_DISPLAY_LABELS[escrow.counterParty.paymentStatus]
    : 'No Payment';
  const counterpartyStatusColor = escrow.counterParty
    ? PAYMENT_ORDER_STATUS_COLORS[escrow.counterParty.paymentStatus]
    : 'text-gray-500';
  const counterpartyRole = isBuyer ? 'Seller' : 'Buyer';

  const activeDispute = getActiveDispute(escrow.disputes);

  const purchasePrice = +escrow.price * +escrow.quantity;
  const depositAmount = +(isBuyer ? escrow.buyerEscrowDepositAmount : escrow.sellerEscrowDepositAmount);
  const feeAmount = +(isBuyer ? escrow.exchangeFeeBuyer : escrow.exchangeFeeSeller);
  const remainderAmount = purchasePrice - depositAmount;
  const depositPercent = purchasePrice > 0 ? (depositAmount / purchasePrice) * 100 : 0;
  const feePercent = purchasePrice > 0 ? (feeAmount / purchasePrice) * 100 : 0;
  const formatAmount = currencyFormatter(escrow.currency).format;

  return (
    <div id="escrow-panel" className={'bg-brand-purple-card rounded-lg p-4 md:p-6'}>
      {escrow.closedAt && escrow.activatedAt && (
        <div className="rounded-lg p-3 mb-4 text-sm font-medium bg-gray-100 text-gray-700 border border-gray-300">
          Escrow Closed
        </div>
      )}
      {!escrow.closedAt && escrow.activatedAt && (
        <div className={'mb-3'}>
          <span className={'rounded-full bg-green-100 text-green-700 text-xs font-semibold px-3 py-1.5'}>
            Escrow Active
          </span>
        </div>
      )}

      <div className={'flex flex-col gap-2 mb-4 md:max-w-md'}>
        <p className={'text-sm font-semibold text-brand-black mb-1'}>Payment Breakdown</p>

        <div className={'flex justify-between text-sm text-brand-black'}>
          <span>Purchase price</span>
          <span className={'font-medium'}>{formatAmount(purchasePrice)}</span>
        </div>

        <div className={'border-t border-gray-300 my-2'} />

        <div className={'flex justify-between text-sm text-brand-black'}>
          <span>Escrow deposit ({depositPercent.toFixed(1)}%)</span>
          <span className={'font-medium'}>{formatAmount(depositAmount)}</span>
        </div>

        {isBuyer && (
          <>
            <div className={'flex justify-between text-sm text-brand-black'}>
              <span>Remainder</span>
              <span className={'font-medium'}>{formatAmount(remainderAmount)}</span>
            </div>

            <div className={'border-t border-gray-300 my-2'} />

            <div className={'flex justify-between text-sm font-semibold text-brand-black'}>
              <span>Total price</span>
              <span>{formatAmount(purchasePrice)}</span>
            </div>
          </>
        )}

        <div className={'flex justify-between text-sm text-brand-black'}>
          <span>Fee ({feePercent.toFixed(1)}%)</span>
          <span className={'font-medium'}>{formatAmount(feeAmount)}</span>
        </div>

        <div className={'border-t border-gray-300 my-2'} />

        <div className={'flex justify-between text-sm font-bold text-brand-black'}>
          <span>Total amount to pay</span>
          <span>{formatAmount(+myPaymentOrder.amount)}</span>
        </div>
      </div>

      <div className={'flex flex-col gap-2'}>
        <div>
          <p className={'text-xs text-gray-500 mb-0.5'}>Your Payment Status</p>
          <p className={`text-sm font-semibold ${statusColorClass}`}>{statusLabel}</p>
          {showPaymentDetail && myPaymentOrder.paymentDriver && (
            <div className="mt-1 flex flex-col gap-0.5">
              <div className="flex items-center gap-1.5">
                <p className="text-xs text-gray-500">
                  Paid via {getPaymentLabel(myPaymentOrder.paymentDriver, myPaymentOrder.paymentMethod)}
                </p>
                <PaymentIcon
                  isCompact
                  name={myPaymentOrder.paymentMethod ?? myPaymentOrder.paymentDriver}
                  url={methodIconUrl ?? driverIconUrl}
                />
              </div>
              {myPaymentOrder.paymentHeldAt && (
                <p className="text-xs text-gray-400">
                  {dayjs(myPaymentOrder.paymentHeldAt).format('D MMM YYYY, h:mm A')}
                </p>
              )}
            </div>
          )}
          {escrow.closedAt && !anyPaymentEverHeld && (
            <p className="mt-1 text-xs text-gray-500">Payment is no longer required — this escrow has been closed.</p>
          )}
        </div>
        <div>
          <p className={'text-xs text-gray-500 mb-0.5'}>{counterpartyRole}&apos;s Payment Status</p>
          <p className={`text-sm font-semibold ${counterpartyStatusColor}`}>{counterpartyStatusLabel}</p>
        </div>
      </div>

      {escrow.disputes.length > 0 && activeDispute && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <p className="text-sm font-semibold text-brand-black mb-2">
            {hasOnlyTerminalDisputes(escrow.disputes) ? 'Latest Dispute' : 'Active Dispute'}
          </p>
          <Link to={`/disputes/${activeDispute.id}`} className="text-sm font-semibold text-blue-600 hover:underline">
            View Dispute Details
          </Link>
        </div>
      )}

      {/* settlement badge — only shown when trade is completed */}
      {tradeStatus === OrderStatus.Completed && <SettlementBadgeChip payouts={escrow.payouts} />}

      {/* payout components panel — shown when payout data is present */}
      {escrow.payouts && <PayoutComponentsPanel payouts={escrow.payouts} escrowId={escrow.id} tradeId={tradeId} />}
    </div>
  );
}

/** Renders a settlement status badge for completed trades. */
function SettlementBadgeChip({ payouts }: { payouts?: EscrowPayouts }) {
  const badge = getSettlementBadge(payouts);

  return (
    <div className="mt-4 pt-3 border-t border-gray-200">
      <span className={`inline-block rounded-full text-xs font-semibold px-3 py-1.5 ${badge.colour}`}>
        {badge.label}
      </span>
    </div>
  );
}

/** Renders per-role, per-component payout status rows. */
function PayoutComponentsPanel({
  escrowId,
  payouts,
  tradeId,
}: {
  escrowId: string;
  payouts: EscrowPayouts;
  tradeId: number;
}) {
  const shapeVersion = getPayoutShapeVersion(payouts);

  // fire analytics event once when payout status is viewed
  const trackedRef = useRef(false);
  useEffect(() => {
    if (!trackedRef.current) {
      trackedRef.current = true;
      logEvent(KEYS.payout_status_viewed, { escrowId, payoutShapeVersion: shapeVersion, tradeId });
    }
  }, [escrowId, shapeVersion, tradeId]);

  // track individual component failures
  const failureTrackedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const [role, summary] of Object.entries(payouts) as [string, EscrowPayoutRoleSummary][]) {
      for (const comp of summary.components) {
        if (
          (comp.status === 'failed' || comp.status === 'reversed') &&
          !failureTrackedRef.current.has(`${role}:${comp.component}`)
        ) {
          failureTrackedRef.current.add(`${role}:${comp.component}`);
          logEvent(KEYS.payout_component_failed, {
            component: comp.component,
            escrowId,
            failureCode: comp.failureCode,
            payoutShapeVersion: shapeVersion,
            role,
            status: comp.status,
            tradeId,
          });
        }
      }
    }
  }, [escrowId, payouts, tradeId]);

  const roles = (['seller', 'buyer'] as const).filter((r) => payouts[r]);
  if (roles.length === 0) return null;

  const anyFailed = roles.some((r) => payouts[r]!.status === 'failed');

  return (
    <div className="mt-4 pt-3 border-t border-gray-200">
      <p className="text-sm font-semibold text-brand-black mb-3">Payout Status</p>

      <div className="flex flex-col gap-3">
        {roles.map((role) => {
          const summary = payouts[role]!;
          return (
            <div key={role}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-semibold text-brand-black capitalize">{role}</span>
                <span
                  className={`inline-block rounded-full text-[10px] font-semibold px-2 py-0.5 ${PAYOUT_ROLE_STATUS_COLORS[summary.status]}`}
                >
                  {PAYOUT_ROLE_STATUS_LABELS[summary.status]}
                </span>
              </div>

              <div className="flex flex-col gap-1 pl-2">
                {summary.components.map((comp, idx) => (
                  <div key={`${comp.component}-${idx}`} className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">{PAYOUT_COMPONENT_LABELS[comp.component]}</span>
                    <span className={`font-medium ${PAYOUT_STATUS_COLORS[comp.status]}`}>
                      {PAYOUT_STATUS_LABELS[comp.status]}
                      {comp.failureCode && (
                        <span className="text-gray-400 ml-1">({getFailureCodeLabel(comp.failureCode)})</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* partial failure helper text (Step 6) */}
      {anyFailed && (
        <p className="text-xs text-gray-500 mt-3">
          A payout failed and is being handled. You do not need to complete the trade again.
        </p>
      )}
    </div>
  );
}
